import { useRef, useState, useEffect } from "react";
import { ChevronDownIcon, Pencil, Trash2 } from "lucide-react";
import DateTimePicker from "../../components/DateTimePicker";
import {
  SelectField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import fileIcon from "../../assets/svg/file.svg";
import {
  getFilingDetails,
  getDefaultSupportingDocuments,
  updateFilingDetails,
  getLeadDetails,
  getFilingDetailsWhatsAppPreview,
  sendFilingDetailsWhatsApp,
  sendMarkImageWhatsApp,
  uploadMarkImage,
  type UpdateFilingDetailsPayload,
  type SupportingDocumentInput,
  type DefaultSupportingDocument,
  type FilingDetailsResponse,
} from "../../api/discussion";
import {
  normalizeApiDateToIsoForForm,
  parseFilingApiDateString,
  toLocalYmd,
} from "../../utils/filingApiDates";
import { toast } from "sonner";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import FilePreviewModal from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import { getWorkflowStatus } from "api/applications";
import { OPPOSITION_KERALA_DISTRICTS } from "api/opposition";

/**
 * Inline date parser — handles DD-MM-YYYY and YYYY-MM-DD without regex.
 * The imported `parseFilingApiDateString` returns null for valid DD-MM-YYYY
 * strings in this environment for reasons we couldn't track down (possibly a
 * babel/CRA transform issue with the regex literal). This local parser sidesteps it.
 */
function isAllDigitsStr(s: string): boolean {
  if (!s || s.length === 0) return false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}

type DateParts = { year: number; month: number; day: number };

const MONTH_NAMES_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Parse a docDate string into {year, month, day}, returning null on invalid input.
 * Avoids using Date entirely because a Chrome extension on this user's environment
 * intermittently patches Date.prototype.getDate, breaking any round-trip validation.
 */
function parseDocDateParts(s: string | null | undefined): DateParts | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t) return null;
  const parts = t.split("-");
  if (parts.length !== 3) return null;
  if (
    !isAllDigitsStr(parts[0]) ||
    !isAllDigitsStr(parts[1]) ||
    !isAllDigitsStr(parts[2])
  ) {
    return null;
  }
  let year: number;
  let month: number;
  let day: number;
  if (parts[0].length === 4) {
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else if (parts[2].length === 4) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return null;
  }
  return { year, month, day };
}

function partsToIsoYmd(p: DateParts): string {
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

function partsToFriendly(p: DateParts): string {
  return `${p.day} ${MONTH_NAMES_SHORT[p.month - 1]} ${p.year}`;
}

/** Format a date string into DD-MM-YYYY; returns the original string if unparseable. */
function formatDateDdMmYyyy(s: string | null | undefined): string {
  const p = parseDocDateParts(s);
  if (!p) return s ?? "";
  return `${String(p.day).padStart(2, "0")}-${String(p.month).padStart(2, "0")}-${p.year}`;
}

function isValidDocDate(s: string | null | undefined): boolean {
  return parseDocDateParts(s) !== null;
}

function normalizeDocDateToIso(s: string | null | undefined): string | undefined {
  const p = parseDocDateParts(s);
  return p ? partsToIsoYmd(p) : undefined;
}


/** Valid ISO 8601 date string (YYYY-MM-DD). Empty string is invalid. */
function isValidIsoDateString(s: string): boolean {
  if (!s || typeof s !== "string") return false;
  const trimmed = s.trim();
  if (!trimmed) return false;
  const match = /^\d{4}-\d{2}-\d{2}$/.exec(trimmed);
  if (!match) return false;
  const d = new Date(trimmed);
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === trimmed;
}

const LABEL_STYLE =
  "text-xs font-normal leading-5 text-[#222222] flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS = "text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS = "text-xs font-normal leading-5 text-[#222222]";

// Backend SignatoryGuardian enum
const GUARDIAN_OPTIONS = [
  { value: "FATHER", label: "Father" },
  { value: "MOTHER", label: "Mother" },
  { value: "HUSBAND", label: "Husband" },
  { value: "WIFE", label: "Wife" },
  { value: "OTHERS", label: "Others" },
];

// Backend accepts string for gender
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

function computeSignatoryRelationClient(
  guardian: string,
  gender: string,
  guardianOther: string
): string {
  const g = (gender || "").toLowerCase();
  const isMale = g === "male";
  const isFemale = g === "female";

  if (guardian === "FATHER" || guardian === "MOTHER") {
    if (isMale) return "S/o";
    if (isFemale) return "D/o";
    return "C/o";
  }

  if (guardian === "HUSBAND" || guardian === "WIFE" || guardian === "OTHERS") {
    return guardian === "OTHERS" && guardianOther.trim() ? "C/o" : "C/o";
  }

  return "";
}

const APPLICANT_TYPE_OPTIONS = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others (manually enter)" },
];

const REQUIRES_PARTNERS_OR_TRUSTEES = [
  "PARTNERSHIP_FIRM",
  "LIMITED_LIABILITY_PARTNERSHIP",
  "TRUST",
];

/** Calculate age in years from date of birth */
function ageFromDob(birthDate: Date | null): number | null {
  if (!birthDate || isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 0 ? age : null;
}

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      className="flex w-full min-w-0 flex-1 flex-col gap-1"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={LABEL_FEATURES}>
        {label}
      </label>
      <div className={INPUT_BASE}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`bg-transparent min-w-0 flex-1 text-[12px] focus:outline-none md:text-xs ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-1"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={LABEL_FEATURES}>
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`box-border flex min-h-[80px] w-full resize-y rounded-[6px] border border-[#EEEEEE] bg-white px-[10px] py-2 text-[12px] font-normal leading-5 focus:outline-none focus:ring-1 focus:ring-[#222] md:min-h-0 md:text-xs ${
          value ? TEXT_CLASS : PLACEHOLDER_CLASS
        }`}
        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      />
    </div>
  );
}

function ImageOfMarkField({
  label,
  fileName,
  onFileChange,
  onUpload,
  disabled,
}: {
  label: string;
  fileName: string;
  onFileChange: (file: File | null) => void;
  onUpload?: (file: File) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      onFileChange(null);
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
      toast.error(
        "Unsupported file format. Please upload JPEG/JPG/PNG only (max 5MB)."
      );
      e.target.value = "";
      onFileChange(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(
        "File size exceeds 5MB limit. Please upload a smaller image."
      );
      e.target.value = "";
      onFileChange(null);
      return;
    }

    onFileChange(file);
    if (file && onUpload) {
      onUpload(file);
    }
  };

  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-2"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={LABEL_FEATURES}>
        {label}
      </label>
      <p className="text-[11px] leading-4 text-[#6D6D6D]">
        Choose a file and upload securely to proceed.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        className="box-border flex min-h-[96px] w-full cursor-pointer flex-col flex-wrap items-stretch gap-3 rounded-[12px] border-2 border-dashed border-[#D8DAEB] bg-white p-4 transition-colors hover:border-[#222222] hover:bg-[#F7F7F7] sm:flex-row sm:items-center md:rounded-[16px]"
        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center sm:h-[60px] sm:w-[60px]">
          <img src={fileIcon} alt="File" className="h-10 w-10" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[12px] font-medium leading-4 text-[#101111] md:text-xs">
            Drag and drop your files
          </span>
          <span className="text-[11px] leading-4 text-[#7D7D7D]">
            Supported formats: JPEG/JPG/PNG. Max size: 5MB.
          </span>
          {fileName && (
            <span className="mt-1 truncate text-[11px] leading-4 text-[#222222]">
              Selected: {fileName}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          disabled={disabled}
          className="inline-flex min-h-[44px] w-full touch-manipulation items-center justify-center whitespace-nowrap rounded-[10px] border border-[#D5D3E0] bg-white px-4 text-[12px] font-medium leading-5 text-[#2E3037] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:w-auto md:text-xs"
        >
          Select File
        </button>
      </div>
    </div>
  );
}

type FilingDetailsToClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

type UserDatePerMarkRow = {
  classNumber: number;
  typeOfMark: string;
  userDateType?: string;
  date?: string;
  supportingDocuments?: SupportingDocumentInput[];
};

export default function FilingDetailsToClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: FilingDetailsToClientViewProps) {
  // Applicant (2a–2d)
  const [applicant, setApplicant] = useState("");
  const [applicantAddressLine, setApplicantAddressLine] = useState("");
  const [applicantAddressDistrict, setApplicantAddressDistrict] = useState("");
  const [applicantAddressState, setApplicantAddressState] = useState("Kerala");
  const [applicantAddressCountry, setApplicantAddressCountry] = useState("India");
  const [applicantType, setApplicantType] = useState("");
  const [applicantTypeOther, setApplicantTypeOther] = useState("");
  const [partnersOrTrustees, setPartnersOrTrustees] = useState("");
  // Signatory (2e–2j)
  const [signatoryName, setSignatoryName] = useState("");
  const [dob, setDob] = useState<Date | null>(null);
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [guardianType, setGuardianType] = useState("");
  const [guardianTypeOther, setGuardianTypeOther] = useState("");
  const [signatoryGuardianName, setSignatoryGuardianName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [signatoryRelationWithGuardian, setSignatoryRelationWithGuardian] =
    useState<string | null>(null);
  // Mark (2k–2m)
  const [imageOfMarkKey, setImageOfMarkKey] = useState("");
  const [imageOfMarkFileName, setImageOfMarkFileName] = useState("");
  const [imageOfMarkUrl, setImageOfMarkUrl] = useState<string | null>(null);
  const [imageOfMarkPreview, setImageOfMarkPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);
  const [userDatesPerMark, setUserDatesPerMark] = useState<
    UserDatePerMarkRow[]
  >([]);
  // Fetched (read-only)
  const [fetched, setFetched] = useState<{
    mark: string | null;
    selectedClasses: number[];
    noOfApplications: number | null;
    goodsServicesPerClass: { classNumber: number; goodsServices: string[] }[];
    selectedTypesOfMark: string[];
    marks: { classNumber: number; typeOfMark: string; date?: string }[];
    outstandingAmount: number | null;
  } | null>(null);
  const [defaultSupportingDocuments, setDefaultSupportingDocuments] = useState<
    DefaultSupportingDocument[]
  >([]);
  const [editingDoc, setEditingDoc] = useState<{
    rowIdx: number;
    docIdx: number;
    doc: SupportingDocumentInput;
  } | null>(null);
  const [editForm, setEditForm] = useState<{
    docName: string;
    docDetail: string;
    docDate: string;
  }>({ docName: "", docDetail: "", docDate: "" });

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [messagePreview, setMessagePreview] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [hasSentMarkImage, setHasSentMarkImage] = useState(false);
  const [resendingMarkImage, setResendingMarkImage] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [invalidSupportingRows, setInvalidSupportingRows] = useState<number[]>(
    []
  );
  const [supportingDocPickerKey, setSupportingDocPickerKey] = useState(0);
  const [collapsedDocRows, setCollapsedDocRows] = useState<
    Record<string, boolean>
  >({});

  const resolveUserDateMode = (
    item: UserDatePerMarkRow
  ): "PROPOSED_TO_BE_USED" | "SPECIFIC_DATE" =>
    (item.userDateType ??
      (item.date ? "SPECIFIC_DATE" : "PROPOSED_TO_BE_USED")) as
      | "PROPOSED_TO_BE_USED"
      | "SPECIFIC_DATE";

  const isProposedUserDateRow = (item: UserDatePerMarkRow): boolean =>
    resolveUserDateMode(item) === "PROPOSED_TO_BE_USED";

  const applyFilingResponse = (
    filingRes: FilingDetailsResponse,
    leadRes?: { lead?: { phone?: string } },
    previewRes?: { message?: string }
  ) => {
    const fd = filingRes.filingDetails;
    setApplicant(fd.applicant ?? "");
    setApplicantAddressLine(fd.applicantAddressLine ?? "");
    setApplicantAddressDistrict(fd.applicantAddressDistrict ?? "");
    setApplicantAddressState(fd.applicantAddressState ?? "Kerala");
    setApplicantAddressCountry(fd.applicantAddressCountry ?? "India");
    setApplicantType(fd.applicantType ?? "");
    setApplicantTypeOther(fd.applicantTypeOther ?? "");
    setPartnersOrTrustees(fd.partnersOrTrustees ?? "");
    setSignatoryName(fd.signatoryName ?? "");
    setDob(
      fd.signatoryDateOfBirth
        ? parseFilingApiDateString(fd.signatoryDateOfBirth)
        : null
    );
    setAge(fd.signatoryAge != null ? String(fd.signatoryAge) : "");
    setGender(fd.signatoryGender ?? "");
    setGuardianType(fd.signatoryGuardian ?? "");
    setGuardianTypeOther("");
    setSignatoryGuardianName(fd.signatoryGuardianName ?? "");
    setSignatoryDesignation(fd.signatoryDesignation ?? "");
    setSignatoryRelationWithGuardian(
      filingRes.signatoryRelationWithGuardian ?? null
    );
    setImageOfMarkKey(fd.imageOfMarkKey ?? "");
    setImageOfMarkFileName(fd.imageOfMarkKey ? "Image uploaded" : "");
    setImageOfMarkUrl(fd.imageOfMarkUrl ?? null);

    const udpm = fd.userDatesPerMark ?? [];
    console.log("[FilingDetails] applyFilingResponse - raw udpm from BE:", JSON.stringify(udpm, null, 2));
    const hasMarks = (filingRes.fetched?.marks?.length ?? 0) > 0;
    const nextUserDates: UserDatePerMarkRow[] =
      udpm.length > 0
        ? udpm.map((item) => {
            const isProposed =
              item.userDateType === "PROPOSED_TO_BE_USED" || !item.date;
            const dateIso = item.date
              ? normalizeApiDateToIsoForForm(item.date) ?? item.date
              : undefined;
            const supportingDocuments = isProposed
              ? []
              : (item.supportingDocuments ?? []).map((doc) => {
                  const normalized =
                    normalizeDocDateToIso(doc.docDate) ?? doc.docDate ?? "";
                  return { ...doc, docDate: normalized };
                });
            return {
              classNumber: item.classNumber,
              typeOfMark: item.typeOfMark,
              userDateType: item.userDateType,
              date: dateIso,
              supportingDocuments,
            };
          })
        : hasMarks
        ? (filingRes.fetched?.marks ?? []).map((m) => {
            const dateIso = m.date
              ? normalizeApiDateToIsoForForm(m.date) ?? m.date
              : undefined;
            return {
              classNumber: m.classNumber,
              typeOfMark: m.typeOfMark,
              userDateType: dateIso ? "SPECIFIC_DATE" : "PROPOSED_TO_BE_USED",
              date: dateIso,
              supportingDocuments: [] as SupportingDocumentInput[],
            };
          })
        : [];
    console.log("[FilingDetails] applyFilingResponse - state nextUserDates:", JSON.stringify(nextUserDates, null, 2));
    setUserDatesPerMark(nextUserDates);

    setFetched(
      filingRes.fetched
        ? {
            mark: filingRes.fetched.mark,
            selectedClasses: filingRes.fetched.selectedClasses,
            noOfApplications: filingRes.fetched.noOfApplications,
            goodsServicesPerClass:
              filingRes.fetched.goodsServicesPerClass ?? [],
            selectedTypesOfMark: filingRes.fetched.selectedTypesOfMark ?? [],
            marks: (filingRes.fetched.marks ?? []).map((m) => ({
              ...m,
              date: m.date
                ? normalizeApiDateToIsoForForm(m.date) ?? m.date
                : undefined,
            })),
            outstandingAmount:
              filingRes.fetched.outstandingAmount ?? null,
          }
        : null
    );
    setMessagePreview(previewRes?.message ?? "");
  };

  useEffect(() => {
    console.log(
      "[FilingDetails] userDatesPerMark state changed:",
      JSON.stringify(
        userDatesPerMark.map((r) => ({
          classNumber: r.classNumber,
          typeOfMark: r.typeOfMark,
          date: r.date,
          docs: (r.supportingDocuments ?? []).map((d) => ({
            docNo: d.docNo,
            docName: d.docName,
            docDate: d.docDate,
          })),
        })),
        null,
        2,
      ),
    );
  }, [userDatesPerMark]);

  // Fetch default supporting documents on mount (no leadId needed)
  useEffect(() => {
    let cancelled = false;
    getDefaultSupportingDocuments()
      .then((docs) => {
        if (!cancelled) setDefaultSupportingDocuments(docs);
      })
      .catch(() => {
        if (!cancelled) setDefaultSupportingDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFilingDetails(leadId),
      getLeadDetails(leadId),
      getFilingDetailsWhatsAppPreview(leadId),
    ])
      .then(([filingRes, leadRes, previewRes]) => {
        if (cancelled) return;
        applyFilingResponse(filingRes, leadRes, previewRes);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load filing details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (invalidSupportingRows.length > 0) {
      setInvalidSupportingRows([]);
    }
  }, [userDatesPerMark, invalidSupportingRows.length]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      setHasSentMarkImage(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        // "Resend to WhatsApp" must depend on the step that indicates WhatsApp was sent.
        // Backend uses `S2_03B_FILING_DETAILS_SENT_TO_CLIENT` for the WhatsApp-sent marker.
        const step = status.steps.find(
          (s: any) => s.slug === "S2_03B_FILING_DETAILS_SENT_TO_CLIENT"
        );
        const markImageStep = status.steps.find(
          (s: any) => s.slug === "S2_03D_MARK_IMAGE_SENT_TO_CLIENT_WHATSAPP"
        );
        if (!cancelled) {
          if (step) {
            setHasSentWhatsapp(step.isCompleted);
            onSentStatusChange?.(step.isCompleted);
          }
          setHasSentMarkImage(!!markImageStep?.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          setHasSentMarkImage(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  useEffect(() => {
    if (
      !REQUIRES_PARTNERS_OR_TRUSTEES.includes(applicantType) &&
      partnersOrTrustees
    ) {
      setPartnersOrTrustees("");
    }
  }, [applicantType, partnersOrTrustees]);

  const handleUploadMarkImage = (file: File) => {
    if (!leadId) return;
    setUploading(true);
    uploadMarkImage(leadId, file)
      .then(({ imageOfMarkKey: key, imageOfMarkUrl: url }) => {
        setImageOfMarkKey(key);
        setImageOfMarkFileName(file.name);
        setImageOfMarkUrl(url ?? null);
        toast.success("Image of mark uploaded successfully.");
      })
      .catch((err) => {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Failed to upload image. Please use JPEG/JPG/PNG and max size 5MB.";
        toast.error(message);
      })
      .finally(() => setUploading(false));
  };

  const openImageOfMarkPreview = () => {
    if (!imageOfMarkUrl) return;
    const name = imageOfMarkFileName || "Image of mark";
    // Image of Mark only ever accepts images (JPEG/JPG/PNG). On revisit the
    // original filename isn't returned (name falls back to "Image uploaded"),
    // so we can't rely on extension detection — always preview as an image.
    setImageOfMarkPreview({ url: imageOfMarkUrl, name, kind: "image" });
  };

  const isSpecificUserDateRow = (item: UserDatePerMarkRow): boolean =>
    resolveUserDateMode(item) === "SPECIFIC_DATE" && Boolean(item.date?.trim());

  const validateFilingDetailsBeforeSave = (): boolean => {
    const rowsMissingSpecificDate = userDatesPerMark.filter(
      (item) => !isProposedUserDateRow(item) && !item.date?.trim()
    );
    if (rowsMissingSpecificDate.length > 0) {
      toast.error(
        'Please select a user date for each application set to "Specific date", or switch it to "Proposed to be used".'
      );
      return false;
    }

    const rowsMissingSupportingDocs = userDatesPerMark
      .map((item, idx) => ({ item, idx }))
      .filter(
        ({ item }) =>
          isSpecificUserDateRow(item) &&
          (!item.supportingDocuments || item.supportingDocuments.length === 0)
      )
      .map(({ idx }) => idx);

    const rowsWithIncompleteSupportingDocs = userDatesPerMark
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => {
        if (!isSpecificUserDateRow(item) || !item.supportingDocuments?.length)
          return false;
        return item.supportingDocuments.some(
          (doc) =>
            !doc.docName?.trim() ||
            !doc.docDetail?.trim() ||
            !isValidIsoDateString(doc.docDate ?? "")
        );
      })
      .map(({ idx }) => idx);

    const invalidRows = Array.from(
      new Set([
        ...rowsMissingSupportingDocs,
        ...rowsWithIncompleteSupportingDocs,
      ])
    );
    if (invalidRows.length > 0) {
      setInvalidSupportingRows(invalidRows);
      toast.error(
        "Please upload and fill all supporting documents for each selected user date."
      );
      return false;
    }

    const invalidDoc = userDatesPerMark
      .flatMap((item, rowIdx) =>
        isSpecificUserDateRow(item)
          ? (item.supportingDocuments ?? []).map((doc) => ({
              rowIdx,
              docDate: doc.docDate,
            }))
          : []
      )
      .find((d) => !isValidIsoDateString(d.docDate ?? ""));
    if (invalidDoc) {
      const msg =
        "All supporting documents must have a valid date (YYYY-MM-DD). Please enter a date for each document.";
      toast.error(msg);
      return false;
    }

    return true;
  };

  const buildFilingDetailsUpdatePayload = (): UpdateFilingDetailsPayload => ({
    applicant: applicant || undefined,
    applicantAddressLine: applicantAddressLine || undefined,
    applicantAddressDistrict: applicantAddressDistrict || undefined,
    applicantAddressState: applicantAddressState || undefined,
    applicantAddressCountry: applicantAddressCountry || undefined,
    applicantType: applicantType || undefined,
    applicantTypeOther: applicantTypeOther || undefined,
    partnersOrTrustees: partnersOrTrustees || undefined,
    signatoryName: signatoryName || undefined,
    signatoryDateOfBirth: dob ? toLocalYmd(dob) : undefined,
    signatoryAge: age ? parseInt(age, 10) : undefined,
    signatoryGender: gender || undefined,
    signatoryGuardian: guardianType || undefined,
    signatoryGuardianName: signatoryGuardianName || undefined,
    signatoryDesignation: signatoryDesignation || undefined,
    imageOfMarkKey: imageOfMarkKey || undefined,
    userDatesPerMark:
      userDatesPerMark.length > 0
        ? userDatesPerMark.map((item) => {
            const hasDate = !!item.date;
            let userDateType =
              item.userDateType ??
              (hasDate ? "SPECIFIC_DATE" : "PROPOSED_TO_BE_USED");
            if (userDateType === "SPECIFIC_DATE" && !hasDate) {
              userDateType = "PROPOSED_TO_BE_USED";
            }
            const specific = userDateType === "SPECIFIC_DATE" && hasDate;
            return {
              classNumber: item.classNumber,
              typeOfMark: item.typeOfMark,
              userDateType,
              date: item.date,
              supportingDocuments:
                specific &&
                item.supportingDocuments &&
                item.supportingDocuments.length > 0
                  ? item.supportingDocuments.map((doc) => ({
                      ...doc,
                      docDate:
                        normalizeDocDateToIso(doc.docDate) ?? doc.docDate,
                    }))
                  : undefined,
            };
          })
        : undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;

    if (!validateFilingDetailsBeforeSave()) return;

    setSaving(true);
    try {
      console.log(
        "[FilingDetails] handleSubmit - userDatesPerMark state at submit:",
        JSON.stringify(userDatesPerMark, null, 2),
      );
      const payload = buildFilingDetailsUpdatePayload();
      console.log(
        "[FilingDetails] handleSubmit - PATCH payload userDatesPerMark:",
        JSON.stringify(payload.userDatesPerMark, null, 2),
      );
      await updateFilingDetails(leadId, payload);
      if (hasSentWhatsapp) {
        toast.success("Filing details saved successfully.");
        if (imageOfMarkKey && !hasSentMarkImage) {
          try {
            await sendMarkImageWhatsApp(leadId);
            setHasSentMarkImage(true);
            toast.success("Mark image sent to the client on WhatsApp.");
          } catch (markErr) {
            toast.error(
              markErr instanceof Error
                ? markErr.message
                : "Failed to send mark image on WhatsApp"
            );
          }
        }
        onSuccess?.(leadId);
      } else {
        const previewRes = await getFilingDetailsWhatsAppPreview(leadId);
        setMessagePreview(previewRes?.message ?? "");
        setConfirmMode("send");
        setShowConfirmModal(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    if (!messagePreview?.trim()) return;
    setShowConfirmModal(false);
    (async () => {
      try {
        const updated = await getFilingDetails(leadId);
        applyFilingResponse(updated);
        if (confirmMode === "resend") {
          await sendFilingDetailsWhatsApp(leadId);
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          toast.success("Filing details resent to client via WhatsApp.");
        } else if (!hasSentWhatsapp) {
          await sendFilingDetailsWhatsApp(leadId);
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          toast.success("Filing details sent to client successfully.");
        } else {
          toast.success("Filing details saved successfully.");
        }

        if (imageOfMarkKey && !hasSentMarkImage) {
          try {
            await sendMarkImageWhatsApp(leadId);
            setHasSentMarkImage(true);
            toast.success("Mark image sent to the client on WhatsApp.");
          } catch (markErr) {
            toast.error(
              markErr instanceof Error
                ? markErr.message
                : "Failed to send mark image on WhatsApp"
            );
          }
        }

        onSuccess?.(leadId);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to save or send"
        );
      }
    })();
  };

  if (!leadId) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col justify-center gap-[22px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col justify-center gap-[22px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading filing details...</p>
      </div>
    );
  }

  const showPartnersOrTrustees =
    REQUIRES_PARTNERS_OR_TRUSTEES.includes(applicantType);
  const showSupportingDocsColumn = userDatesPerMark.some(
    (r) => !isProposedUserDateRow(r)
  );
  const liveSignatoryRelation = computeSignatoryRelationClient(
    guardianType,
    gender,
    guardianTypeOther
  );
  const displayedSignatoryRelation =
    liveSignatoryRelation || signatoryRelationWithGuardian || "";

  return (
    <form
      id="filing-details-to-client-form"
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif", gap: 20 }}
    >
      {/* Section: Applicant (2a–2d) */}
      <div className="flex w-full flex-col gap-4 md:gap-5">
        <h3 className="text-sm font-medium text-[#222222]">
          Applicant Details
        </h3>
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-5">
          <TextField
            label="2a) Applicant Name"
            value={applicant}
            onChange={setApplicant}
          />
          <SelectField
            label="2c) Applicant Type"
            value={applicantType}
            onChange={setApplicantType}
            options={APPLICANT_TYPE_OPTIONS}
            className="[&_button]:min-h-[44px] md:[&_button]:min-h-0"
          />
        </div>
        {applicantType === "OTHERS" && (
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-5">
            <TextField
              label="Applicant Type (Other)"
              value={applicantTypeOther}
              onChange={setApplicantTypeOther}
              placeholder="e.g. Joint Venture, HUF"
            />
          </div>
        )}
        {showPartnersOrTrustees && (
          <div className="flex w-full flex-col">
            <TextAreaField
              label="2d) Partners or Trustees"
              value={partnersOrTrustees}
              onChange={setPartnersOrTrustees}
              placeholder="Enter partners or trustees"
              rows={2}
            />
          </div>
        )}
        <div className="flex w-full flex-col">
          <TextAreaField
            label="2b) Applicant Address"
            value={applicantAddressLine}
            onChange={setApplicantAddressLine}
            placeholder="Street, P.O, Pincode"
            rows={2}
          />
        </div>
        <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-5">
          <SelectField
            label="District"
            value={applicantAddressDistrict}
            onChange={setApplicantAddressDistrict}
            placeholder="Select District"
            options={OPPOSITION_KERALA_DISTRICTS.map((d) => ({
              value: d,
              label: d,
            }))}
            className="[&_button]:min-h-[44px] md:[&_button]:min-h-0"
          />
          <TextField
            label="State"
            value={applicantAddressState}
            onChange={setApplicantAddressState}
          />
          <TextField
            label="Country"
            value={applicantAddressCountry}
            onChange={setApplicantAddressCountry}
          />
        </div>
      </div>

      {/* Section: Signatory (2e–2j) */}
      <div className="flex w-full flex-col gap-4 md:gap-5">
        <h3 className="text-sm font-medium text-[#222222]">
          Signatory Details
        </h3>
        {/* Row 1: Signatory's Name + DOB (Calc Age) */}
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-5">
          <TextField
            label="2e) Signatory's Name"
            value={signatoryName}
            onChange={setSignatoryName}
          />
          <div
            className="flex w-full min-w-0 flex-1 flex-col gap-1"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <label className={LABEL_STYLE} style={LABEL_FEATURES}>
              DOB (Calc Age)
            </label>
            <DateTimePicker
              value={dob}
              onChange={(date) => {
                setDob(date);
                const calculatedAge = ageFromDob(date);
                setAge(calculatedAge != null ? String(calculatedAge) : "");
              }}
              buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs"
            />
          </div>
        </div>

        {/* Row 2: Age + Gender */}
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-5">
          <TextField
            label="2f) Signatory's Age"
            value={age}
            onChange={setAge}
          />
          <SelectField
            label="2g) Signatory's Gender"
            value={gender}
            onChange={setGender}
            options={GENDER_OPTIONS}
            className="[&_button]:min-h-[44px] md:[&_button]:min-h-0"
          />
        </div>

        {/* Row 3: Guardian Type + Guardian Name + Relation (2n read-only) */}
        <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-5">
          <SelectField
            label="2h) Signatory's Guardian"
            value={guardianType}
            onChange={(value) => {
              setGuardianType(value);
              if (value !== "OTHERS") setGuardianTypeOther("");
            }}
            options={GUARDIAN_OPTIONS}
            className="[&_button]:min-h-[44px] md:[&_button]:min-h-0"
          />
          {guardianType === "OTHERS" && (
            <TextField
              label="2h) Signatory's Guardian (Others)"
              value={guardianTypeOther}
              onChange={setGuardianTypeOther}
              placeholder="Enter guardian relation"
            />
          )}
          <TextField
            label="2i) Signatory's Guardian's Name"
            value={signatoryGuardianName}
            onChange={setSignatoryGuardianName}
          />
          {displayedSignatoryRelation && (
            <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
              <label className={LABEL_STYLE} style={LABEL_FEATURES}>
                2n) Signatory's relation with Guardian
              </label>
              <div className={INPUT_BASE}>
                <span className={`${TEXT_CLASS} text-[12px] md:text-xs`}>
                  {displayedSignatoryRelation}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Row 4: Signatory's Designation (full width) */}
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-5">
          <TextField
            label="Signatory's Designation"
            value={signatoryDesignation}
            onChange={setSignatoryDesignation}
            placeholder="Text Here"
          />
        </div>

        {/* Row 5: Image of Mark + optional WhatsApp send (separate from main filing confirmation text) */}
        <div className="flex w-full max-w-[1085px] flex-col gap-3">
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:gap-5">
            <div className="min-w-0 flex-1">
              <ImageOfMarkField
                label="Image of Mark"
                fileName={imageOfMarkFileName}
                onFileChange={() => {}}
                onUpload={handleUploadMarkImage}
                disabled={uploading}
              />
            </div>
            {imageOfMarkUrl ? (
              <div className="flex w-full shrink-0 flex-col gap-1 md:w-auto">
                <span
                  className="text-xs font-normal leading-5 text-[#222222] md:invisible md:h-5"
                  aria-hidden
                >
                  Preview
                </span>
                <button
                  type="button"
                  onClick={openImageOfMarkPreview}
                  className="inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-[10px] border border-[#D5D3E0] bg-white px-4 text-xs font-medium leading-5 text-[#2E3037] hover:bg-gray-50 md:min-h-9"
                >
                  Preview
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!imageOfMarkPreview}
        onClose={() => setImageOfMarkPreview(null)}
        name={imageOfMarkPreview?.name ?? ""}
        url={imageOfMarkPreview?.url}
        kind={imageOfMarkPreview?.kind}
      />

      {/* 2l & 2m) User Date and Supporting Documents per Mark – Figma layout */}
      <div
        className="flex w-full flex-col"
        style={{
          fontFamily: "Inter, sans-serif",
          fontFeatureSettings: "'ss01' on, 'cv01' on",
        }}
      >
        {userDatesPerMark.length > 0 ? (
          <div className="mt-1 flex flex-col gap-4">
            {/* Header row — supporting docs are in a subsection below */}
            <div className="hidden shrink-0 items-end gap-5 text-sm font-medium leading-5 text-[#222] md:flex">
              <div className="w-[160px] shrink-0">Classes</div>
              <div className="min-w-0 flex-1 md:max-w-[360px]">Type Mark</div>
              <div className="w-[140px] shrink-0 text-center leading-tight">
                Proposed / Specific
              </div>
              {showSupportingDocsColumn ? (
                <div className="w-[184px] shrink-0">User date</div>
              ) : null}
            </div>
            {/* Data rows */}
            <div className="flex flex-col gap-3">
              {userDatesPerMark.map((item, idx) => (
                <div
                  key={`${item.classNumber}-${item.typeOfMark}-${idx}`}
                  className="md:bg-transparent flex w-full flex-col gap-4 rounded-[6px] border border-[#EEE] bg-white p-4 md:border-0 md:p-0"
                >
                  <div className="flex w-full flex-col gap-3 md:flex-row md:items-start md:gap-5">
                    {/* Class cell – grey bg, bordered */}
                    <div className="flex min-h-[44px] w-full shrink-0 items-center justify-between gap-2 rounded-[6px] border border-[#eee] bg-[#f7f7f7] p-2.5 md:min-h-0 md:w-[160px]">
                      <span className="whitespace-nowrap text-[12px] text-[#222] md:text-sm">
                        Class {item.classNumber}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#6D6D6D]" />
                    </div>
                    {/* Type Mark – select */}
                    <div className="flex min-w-0 flex-1 flex-col gap-1 md:contents">
                      <span className="text-xs font-normal text-[#222222] md:hidden">
                        Type Mark
                      </span>
                      <div className="min-w-0 flex-1 md:max-w-[360px]">
                        {(fetched?.selectedTypesOfMark?.length ?? 0) > 0 ? (
                          <Select
                            value={item.typeOfMark || ""}
                            onValueChange={(v) => {
                              const next = [...userDatesPerMark];
                              next[idx] = { ...item, typeOfMark: v };
                              setUserDatesPerMark(next);
                            }}
                          >
                            <SelectTrigger className="h-9 min-h-[44px] w-full rounded-[6px] border border-[#eee] px-2.5 py-2 text-[12px] text-[#222] data-[placeholder]:text-[#6D6D6D] md:min-h-[36px] md:text-xs">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {(fetched?.selectedTypesOfMark ?? [])
                                .filter((t) => {
                                  if (!t || !t.trim()) return false;
                                  const usedByOtherRow = userDatesPerMark.some(
                                    (r, i) =>
                                      i !== idx &&
                                      r.classNumber === item.classNumber &&
                                      r.typeOfMark === t
                                  );
                                  return (
                                    !usedByOtherRow || t === item.typeOfMark
                                  );
                                })
                                .map((t) => (
                                  <SelectItem key={t} value={t}>
                                    {t}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex h-9 min-h-[44px] w-full items-center justify-between rounded-[6px] border border-[#eee] bg-white px-2.5 py-2 text-[12px] md:min-h-[36px] md:text-xs">
                            <span
                              className={
                                item.typeOfMark
                                  ? "text-[#222]"
                                  : "text-[#6D6D6D]"
                              }
                            >
                              {item.typeOfMark || "Select an option"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* 2l) User date type — own tile (separate from date field) */}
                    <div className="flex w-full shrink-0 flex-col gap-1 md:w-[140px]">
                      <span className="text-xs font-normal text-[#222222] md:hidden">
                        Proposed / Specific
                      </span>
                      <div
                        className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2 py-2.5 md:min-h-[36px] md:py-2"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        <span
                          className={`shrink-0 text-[10px] leading-tight ${
                            isProposedUserDateRow(item)
                              ? "font-semibold text-[#222]"
                              : "font-normal text-[#9ca3af]"
                          }`}
                          title="Proposed to be used"
                        >
                          Proposed
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!isProposedUserDateRow(item)}
                          title={
                            isProposedUserDateRow(item)
                              ? "Proposed to be used — click for specific date"
                              : "Specific date — click for proposed"
                          }
                          onClick={() => {
                            const next = [...userDatesPerMark];
                            if (isProposedUserDateRow(item)) {
                              next[idx] = {
                                ...item,
                                userDateType: "SPECIFIC_DATE",
                                date: item.date,
                              };
                            } else {
                              next[idx] = {
                                ...item,
                                userDateType: "PROPOSED_TO_BE_USED",
                                date: undefined,
                                supportingDocuments: [],
                              };
                            }
                            setUserDatesPerMark(next);
                          }}
                          className={`relative inline-flex h-[18px] w-[32px] shrink-0 touch-manipulation items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#222] focus-visible:ring-offset-1 ${
                            !isProposedUserDateRow(item)
                              ? "border-[#222] bg-[#222]"
                              : "border-[#e5e5e5] bg-[#ececec]"
                          }`}
                        >
                          <span
                            className={`pointer-events-none absolute left-[2px] top-[2px] h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out ${
                              !isProposedUserDateRow(item)
                                ? "translate-x-[14px]"
                                : "translate-x-0"
                            }`}
                            aria-hidden
                          />
                        </button>
                        <span
                          className={`shrink-0 text-[10px] leading-tight ${
                            !isProposedUserDateRow(item)
                              ? "font-semibold text-[#222]"
                              : "font-normal text-[#9ca3af]"
                          }`}
                          title="Specific date"
                        >
                          Specific
                        </span>
                      </div>
                    </div>
                    {/* 2l) User date — only when Specific; reserves the column on desktop when Proposed */}
                    {!isProposedUserDateRow(item) ? (
                      <div className="flex w-full flex-col gap-1 md:w-[184px] md:shrink-0 md:grow-0">
                        <span className="text-xs font-normal text-[#222222] md:hidden">
                          2l) User date
                        </span>
                        <div className="flex w-full flex-col gap-2">
                          <DateTimePicker
                            value={
                              parseFilingApiDateString(item.date) ?? null
                            }
                            onChange={(d) => {
                              const next = [...userDatesPerMark];
                              if (d) {
                                next[idx] = {
                                  ...item,
                                  date: toLocalYmd(d),
                                  userDateType: "SPECIFIC_DATE",
                                };
                              } else {
                                next[idx] = {
                                  ...item,
                                  date: undefined,
                                  userDateType: "SPECIFIC_DATE",
                                };
                              }
                              setUserDatesPerMark(next);
                            }}
                            placeholder="Select date"
                            buttonClassName="min-h-[44px] md:min-h-0 text-[12px] md:text-xs w-full"
                          />
                        </div>
                      </div>
                    ) : showSupportingDocsColumn ? (
                      <div
                        className="hidden md:block md:w-[184px] md:shrink-0 md:grow-0"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                  {/* 2m) List of Supporting Documents — shown only after a specific User date is chosen */}
                  {showSupportingDocsColumn &&
                  !isProposedUserDateRow(item) &&
                  item.date?.trim() ? (
                    <div
                      className="w-full rounded-[8px] border border-[#e5e7eb] bg-[#f9fafb] p-3 sm:p-4"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {(() => {
                        const docCollapseKey = `${item.classNumber}-${item.typeOfMark}-${idx}`;
                        const isDocsCollapsed = collapsedDocRows[docCollapseKey];
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setCollapsedDocRows((prev) => ({
                                  ...prev,
                                  [docCollapseKey]: !prev[docCollapseKey],
                                }))
                              }
                              aria-expanded={!isDocsCollapsed}
                              className="mb-2 flex w-full items-center justify-between gap-2 text-left sm:mb-3"
                            >
                              <span className="min-w-0">
                                <span className="block text-xs font-medium text-[#222222] sm:text-sm">
                                  List of Supporting Documents
                                </span>
                                {userDatesPerMark.length > 1 ? (
                                  <span className="mt-0.5 block text-[11px] text-[#6d6d6d]">
                                    Class {item.classNumber}
                                  </span>
                                ) : null}
                              </span>
                              <ChevronDownIcon
                                className={`h-4 w-4 shrink-0 text-[#6D6D6D] transition-transform duration-200 ${
                                  isDocsCollapsed ? "-rotate-90" : "rotate-0"
                                }`}
                              />
                            </button>
                            {!isDocsCollapsed ? (
                      <div className="flex w-full min-w-0 flex-1 flex-col gap-1.5">
                        {!item.date?.trim() && (
                          <p className="text-[11px] text-[#6D6D6D]">
                            Select a specific user date for this class, then add
                            supporting documents.
                          </p>
                        )}
                        {invalidSupportingRows.includes(idx) &&
                          item.date?.trim() && (
                            <div className="text-[11px] text-[#E7000B]">
                              Supporting documents are required and must be
                              fully filled.
                            </div>
                          )}
                        {item.date?.trim() &&
                        item.supportingDocuments &&
                        item.supportingDocuments.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {item.supportingDocuments.map((doc, docIdx) => {
                              const isEditing =
                                editingDoc?.rowIdx === idx &&
                                editingDoc?.docIdx === docIdx;
                              return (
                                <div
                                  key={`${item.classNumber}-${idx}-${doc.docNo}`}
                                  className="flex flex-col gap-2"
                                >
                                  {isEditing ? (
                                    <div className="flex flex-col flex-wrap items-stretch gap-2 sm:flex-row sm:items-center">
                                      <input
                                        type="text"
                                        value={editForm.docName}
                                        onChange={(e) =>
                                          setEditForm((prev) => ({
                                            ...prev,
                                            docName: e.target.value,
                                          }))
                                        }
                                        placeholder="Doc Name"
                                        className="h-9 min-h-[44px] min-w-0 flex-1 rounded-[6px] border border-[#eee] px-2.5 py-2 text-[12px] text-[#222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] md:min-h-0 md:text-xs"
                                      />
                                      <input
                                        type="text"
                                        value={editForm.docDetail}
                                        onChange={(e) =>
                                          setEditForm((prev) => ({
                                            ...prev,
                                            docDetail: e.target.value,
                                          }))
                                        }
                                        placeholder="Doc Detail (editable)"
                                        className="h-9 min-h-[44px] w-full min-w-0 flex-1 rounded-[6px] border border-[#eee] px-2.5 py-2 text-[12px] text-[#222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] md:min-h-0 md:text-xs"
                                      />
                                      <div className="w-full shrink-0 sm:w-[182px]">
                                        <input
                                          type="date"
                                          value={
                                            isValidDocDate(editForm.docDate)
                                              ? (parseDocDateParts(
                                                  editForm.docDate,
                                                ) &&
                                                  partsToIsoYmd(
                                                    parseDocDateParts(
                                                      editForm.docDate,
                                                    )!,
                                                  )) ||
                                                ""
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const nextDocDate = e.target.value;
                                            console.log(
                                              `[FilingDetails] native date input change - rowIdx=${idx} docIdx=${docIdx} value="${nextDocDate}"`,
                                            );
                                            setEditForm((prev) => ({
                                              ...prev,
                                              docDate: nextDocDate,
                                            }));
                                            setUserDatesPerMark((prevAll) => {
                                              const next = [...prevAll];
                                              const docs = [
                                                ...(next[idx]
                                                  ?.supportingDocuments ?? []),
                                              ];
                                              if (docs[docIdx]) {
                                                docs[docIdx] = {
                                                  ...docs[docIdx],
                                                  docDate: nextDocDate,
                                                };
                                                next[idx] = {
                                                  ...next[idx],
                                                  supportingDocuments: docs,
                                                };
                                              }
                                              return next;
                                            });
                                          }}
                                          className={`h-9 min-h-[44px] w-full rounded-[6px] border bg-white px-2.5 py-2 text-[12px] text-[#222] focus:outline-none focus:ring-1 md:min-h-0 md:text-xs ${
                                            editForm.docDate
                                              ? "border-[#eee] focus:ring-[#222]"
                                              : "border-[#E7000B] focus:ring-[#E7000B]"
                                          }`}
                                        />
                                      </div>
                                      <div className="flex shrink-0 gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            console.log(
                                              `[FilingDetails] inner Save click - rowIdx=${idx} docIdx=${docIdx} editForm:`,
                                              editForm,
                                            );
                                            const isDuplicate = (
                                              item.supportingDocuments ?? []
                                            ).some(
                                              (d, i) =>
                                                i !== docIdx &&
                                                d.docName.trim().toLowerCase() ===
                                                  editForm.docName
                                                    .trim()
                                                    .toLowerCase() &&
                                                d.docDetail
                                                  .trim()
                                                  .toLowerCase() ===
                                                  editForm.docDetail
                                                    .trim()
                                                    .toLowerCase()
                                            );
                                            if (isDuplicate) {
                                              toast.error(
                                                "This document is already added for this class and type."
                                              );
                                              return;
                                            }
                                            setUserDatesPerMark((prevAll) => {
                                              const next = [...prevAll];
                                              const docs = [
                                                ...(next[idx]
                                                  ?.supportingDocuments ?? []),
                                              ];
                                              docs[docIdx] = {
                                                ...docs[docIdx],
                                                docName: editForm.docName,
                                                docDetail: editForm.docDetail,
                                                docDate: editForm.docDate,
                                              };
                                              next[idx] = {
                                                ...next[idx],
                                                supportingDocuments: docs,
                                              };
                                              console.log(
                                                `[FilingDetails] inner Save - committed doc:`,
                                                docs[docIdx],
                                              );
                                              return next;
                                            });
                                            setEditingDoc(null);
                                          }}
                                          className="box-border inline-flex h-9 min-h-[44px] shrink-0 touch-manipulation items-center justify-center rounded-[6px] bg-[#222] px-3 text-[12px] font-medium text-white hover:bg-[#333] md:min-h-0 md:text-xs"
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            console.log(
                                              `[FilingDetails] CANCEL inner edit click - rowIdx=${idx} docIdx=${docIdx}`,
                                            );
                                            setEditingDoc(null);
                                          }}
                                          className="box-border inline-flex h-9 min-h-[44px] shrink-0 touch-manipulation items-center justify-center rounded-[6px] border border-[#eee] px-3 text-[12px] font-medium text-[#222] hover:bg-[#eee] md:min-h-0 md:text-xs"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (() => {
                                    const parsedParts = parseDocDateParts(
                                      doc.docDate
                                    );
                                    const hasDate = !!parsedParts;
                                    return (
                                    <div
                                      className={`group flex items-start gap-2 text-xs ${
                                        hasDate
                                          ? ""
                                          : "rounded-[6px] border border-[#E7000B] bg-[#FFF5F5] p-2"
                                      }`}
                                    >
                                      <span className="min-w-0 flex-1 break-words">
                                        <span className="font-medium text-[#222]">
                                          {doc.docName || "—"}
                                        </span>
                                        {doc.docDetail && (
                                          <span className="whitespace-pre-line text-[#6D6D6D]">
                                            {" "}
                                            – {doc.docDetail}
                                          </span>
                                        )}
                                        {hasDate ? (
                                          <span className="text-[#6D6D6D]">
                                            {" "}
                                            ({partsToFriendly(parsedParts!)})
                                          </span>
                                        ) : (
                                          <span className="font-medium text-[#E7000B]">
                                            {" "}
                                            — Date required
                                          </span>
                                        )}
                                      </span>
                                      <div className="flex shrink-0 items-center gap-0.5">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            console.log(
                                              `[FilingDetails] PENCIL click - rowIdx=${idx} docIdx=${docIdx} doc:`,
                                              doc,
                                            );
                                            setEditingDoc({
                                              rowIdx: idx,
                                              docIdx,
                                              doc,
                                            });
                                            setEditForm({
                                              docName: doc.docName,
                                              docDetail: doc.docDetail,
                                              docDate: doc.docDate,
                                            });
                                            console.log(
                                              `[FilingDetails] PENCIL click - editForm initialized to:`,
                                              {
                                                docName: doc.docName,
                                                docDetail: doc.docDetail,
                                                docDate: doc.docDate,
                                              },
                                            );
                                          }}
                                          className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded p-2 text-[#6D6D6D] transition-colors hover:bg-[#eee] hover:text-[#222] md:min-h-0 md:min-w-0 md:p-1.5"
                                          aria-label="Edit document"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            console.log(
                                              `[FilingDetails] DELETE doc click - rowIdx=${idx} docIdx=${docIdx} doc:`,
                                              doc,
                                            );
                                            const next = [...userDatesPerMark];
                                            const filtered = (
                                              item.supportingDocuments ?? []
                                            ).filter((_, i) => i !== docIdx);
                                            next[idx] = {
                                              ...item,
                                              supportingDocuments: filtered,
                                            };
                                            setUserDatesPerMark(next);
                                          }}
                                          className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded p-2 text-[#6D6D6D] transition-colors hover:bg-[#fee] hover:text-red-600 md:min-h-0 md:min-w-0 md:p-1.5"
                                          aria-label="Delete document"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                        {item.date?.trim() ? (
                          <div className="flex flex-col gap-2">
                            {defaultSupportingDocuments.length > 0 && (
                              <Select
                                key={`def-doc-${idx}-${supportingDocPickerKey}`}
                                onValueChange={(val) => {
                                  console.log(
                                    `[FilingDetails] DEFAULT-DOC pick - rowIdx=${idx} val="${val}"`,
                                  );
                                  if (!val) return;
                                  const chosen =
                                    defaultSupportingDocuments.find(
                                      (d) =>
                                        `${d.docName}|${d.docDetail}` === val
                                    );
                                  if (!chosen) return;
                                  console.log(
                                    `[FilingDetails] DEFAULT-DOC chosen:`,
                                    chosen,
                                  );
                                  let existing = item.supportingDocuments ?? [];
                                  const isEditingEmptyInRow =
                                    editingDoc?.rowIdx === idx &&
                                    !editForm.docName.trim() &&
                                    !editForm.docDetail.trim() &&
                                    !editForm.docDate.trim();
                                  if (
                                    isEditingEmptyInRow &&
                                    editingDoc &&
                                    existing[editingDoc.docIdx]
                                  ) {
                                    existing = existing.filter(
                                      (_, i) => i !== editingDoc.docIdx
                                    );
                                  }
                                  const isDuplicate = existing.some(
                                    (d) =>
                                      d.docName.trim().toLowerCase() ===
                                        chosen.docName.trim().toLowerCase() &&
                                      d.docDetail.trim().toLowerCase() ===
                                        chosen.docDetail.trim().toLowerCase()
                                  );
                                  if (isDuplicate) {
                                    toast.error(
                                      "This document is already added for this class and type."
                                    );
                                    setSupportingDocPickerKey((k) => k + 1);
                                    return;
                                  }
                                  const nextNo =
                                    existing.length > 0
                                      ? Math.max(
                                          ...existing.map((d) => d.docNo)
                                        ) + 1
                                      : 1;
                                  const newDoc: SupportingDocumentInput = {
                                    docNo: nextNo,
                                    docName: chosen.docName,
                                    docDetail: chosen.docDetail,
                                    docDate:
                                      normalizeDocDateToIso(chosen.docDate) ??
                                      "",
                                  };
                                  const next = [...userDatesPerMark];
                                  next[idx] = {
                                    ...item,
                                    supportingDocuments: [...existing, newDoc],
                                  };
                                  setUserDatesPerMark(next);
                                  const newDocIdx = existing.length;
                                  setEditingDoc({
                                    rowIdx: idx,
                                    docIdx: newDocIdx,
                                    doc: newDoc,
                                  });
                                  setEditForm({
                                    docName: newDoc.docName,
                                    docDetail: newDoc.docDetail,
                                    docDate: newDoc.docDate,
                                  });
                                  setSupportingDocPickerKey((k) => k + 1);
                                }}
                              >
                                <SelectTrigger className="h-9 min-h-[44px] w-full rounded-[6px] border border-[#eee] bg-white px-2.5 py-2 text-[12px] data-[placeholder]:text-[#6D6D6D] md:min-h-[36px] md:text-xs">
                                  <SelectValue placeholder="Add from default list (Doc Detail auto-fills)" />
                                </SelectTrigger>
                                <SelectContent>
                                  {defaultSupportingDocuments.map((d) => (
                                    <SelectItem
                                      key={`${d.docName}-${d.docDetail}`}
                                      value={`${d.docName}|${d.docDetail}`}
                                    >
                                      {d.docName}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                console.log(
                                  `[FilingDetails] ADD CUSTOM doc click - rowIdx=${idx}`,
                                );
                                const existing = item.supportingDocuments ?? [];
                                const nextNo =
                                  existing.length > 0
                                    ? Math.max(
                                        ...existing.map((d) => d.docNo)
                                      ) + 1
                                    : 1;
                                const newDoc: SupportingDocumentInput = {
                                  docNo: nextNo,
                                  docName: "",
                                  docDetail: "",
                                  docDate: "",
                                };
                                const next = [...userDatesPerMark];
                                next[idx] = {
                                  ...item,
                                  supportingDocuments: [...existing, newDoc],
                                };
                                setUserDatesPerMark(next);
                                const newDocIdx = existing.length;
                                setEditingDoc({
                                  rowIdx: idx,
                                  docIdx: newDocIdx,
                                  doc: newDoc,
                                });
                                setEditForm({
                                  docName: newDoc.docName,
                                  docDetail: newDoc.docDetail,
                                  docDate: newDoc.docDate,
                                });
                              }}
                              className="h-auto min-h-[44px] touch-manipulation rounded-[6px] border border-dashed border-[#eee] px-2.5 py-2.5 text-left text-[12px] text-[#6D6D6D] transition-colors hover:bg-[#f7f7f7] hover:text-[#222] md:h-9 md:py-2 md:text-xs"
                            >
                              + Add custom document
                            </button>
                          </div>
                        ) : null}
                      </div>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-1 text-xs text-[#6D6D6D]">No marks to configure</p>
        )}
      </div>

      {/* Fetched data (read-only) */}
      {fetched && (
        <div className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] p-3 md:p-3">
          <span className="text-[12px] font-medium text-[#222222] md:text-xs">
            Fetched Data
          </span>
          <div className="flex flex-col gap-2 break-words text-[12px] text-[#6D6D6D] md:text-xs">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {fetched.mark && <span>Mark: {fetched.mark}</span>}
              {fetched.selectedClasses?.length > 0 && (
                <span>Classes: {fetched.selectedClasses.join(", ")}</span>
              )}
              {fetched.selectedTypesOfMark?.length > 0 && (
                <span>
                  Types of Mark: {fetched.selectedTypesOfMark.join(", ")}
                </span>
              )}
              {fetched.noOfApplications != null && (
                <span>No. of Applications: {fetched.noOfApplications}</span>
              )}
              {fetched.outstandingAmount != null && (
                <span>
                  Outstanding Amount (₹): {fetched.outstandingAmount}
                </span>
              )}
            </div>
            {fetched.goodsServicesPerClass?.length > 0 && (
              <div className="mt-1">
                <span className="font-medium text-[#222222]">
                  Goods/Services per class:
                </span>
                {fetched.goodsServicesPerClass.map((c) => (
                  <div key={c.classNumber} className="ml-2 mt-1">
                    <span className="font-medium">Class {c.classNumber}:</span>{" "}
                    {c.goodsServices?.join(", ") || "—"}
                  </div>
                ))}
              </div>
            )}
            {fetched.marks?.length > 0 && (
              <div className="mt-1">
                <span className="font-medium text-[#222222]">
                  Marks (Class + Type):
                </span>
                {fetched.marks.map((m, idx) => (
                  <div
                    key={`${m.classNumber}-${m.typeOfMark}-${idx}`}
                    className="ml-2 mt-1"
                  >
                    Class {m.classNumber} – {m.typeOfMark}
                    {m.date && (
                      <span className="ml-1">
                        (Date: {formatDateDdMmYyyy(m.date)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={messagePreview || "—"}
        onConfirm={handleConfirmProceed}
      />
      {/* Resend buttons go right after Fetched Data */}
      {leadId && (hasSentWhatsapp || hasSentMarkImage) && (
        <div className="flex flex-wrap items-center gap-3">
          {hasSentWhatsapp && (
            <button
              type="button"
              disabled={resending}
              onClick={async () => {
                if (!leadId || resending) return;
                if (!validateFilingDetailsBeforeSave()) return;
                setResending(true);
                try {
                  const payload = buildFilingDetailsUpdatePayload();
                  const updated = await updateFilingDetails(leadId, payload);
                  const previewRes = await getFilingDetailsWhatsAppPreview(
                    leadId
                  );
                  applyFilingResponse(updated, undefined, previewRes);
                  setConfirmMode("resend");
                  setShowConfirmModal(true);
                } catch (err) {
                  toast.error(
                    err instanceof Error
                      ? err.message
                      : "Failed to resend filing details"
                  );
                } finally {
                  setResending(false);
                }
              }}
              className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-[10px] border border-[#D5D3E0] bg-white px-4 text-xs font-medium leading-5 text-[#2E3037] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resending ? "Resending…" : "Resend to WhatsApp"}
            </button>
          )}
          {hasSentMarkImage && (
            <button
              type="button"
              disabled={
                resendingMarkImage || !imageOfMarkKey || uploading
              }
              onClick={async () => {
                if (!leadId || resendingMarkImage || !imageOfMarkKey) return;
                setResendingMarkImage(true);
                try {
                  await sendMarkImageWhatsApp(leadId);
                  toast.success("Mark image resent to the client on WhatsApp.");
                } catch (err) {
                  toast.error(
                    err instanceof Error
                      ? err.message
                      : "Failed to resend mark image on WhatsApp"
                  );
                } finally {
                  setResendingMarkImage(false);
                }
              }}
              className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-[10px] border border-[#D5D3E0] bg-white px-4 text-xs font-medium leading-5 text-[#2E3037] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendingMarkImage ? "Resending…" : "Resend mark image"}
            </button>
          )}
        </div>
      )}

      {saving && <p className="text-xs text-[#6D6D6D]">Saving...</p>}
    </form>
  );
}
